<script>
  import Page from '@components/page'
  import Text from '@components/general/text.svelte'
  import Button from '@components/buttons/button.svelte'
  import Block from '@components/general/block'
  import Hero from '@components/live_sessions/hero.svelte'
  import Card from '@components/live_sessions/card.svelte'
  import ContributorBlock from '@components/contributors/info-block.svelte'
  import AddToCalendar from '@components/general/add-to-calendar.svelte'
  import Tag from '@components/general/tag.svelte'
  import FeedbackFab from '@components/feedbacks/fab.svelte'
  import ImageThumbnails from '@components/general/image-thumbnails.svelte'
  import { format, differenceInMinutes } from 'date-fns'
  import { formatTime } from '@lib/utils/date'
  import { Video, ChevronRight, Pencil, RefreshCw, Settings } from 'lucide-svelte'
  import { useForm, router, page } from '@inertiajs/svelte'
  import { colors } from '@lib/utils/colors'
  import { cn } from '@lib/utils'
  import { formatDuration } from '@lib/utils/date'

  let { live_session, feedback, upcoming_sessions = [] } = $props()

  let user = $page.props.user
  let canDownloadCalendar = $derived(user?.is_admin || user?.has_active_membership)
  
  let title = $derived(live_session?.event_name || "Live Session")
  let color = $derived(live_session?.color || 'gray')

  let duration = $derived(formatDuration(live_session.start_time, live_session.end_time))

  const sync = useForm()

  function syncSession() {
    $sync.post(`/admin/live_sessions/${live_session.id}/sync`)
  }
</script>

<Page.Layout {title}>

  <Page.Navigation {title} Icon={Video}
    links={[{ href: '/live-sessions', text: 'Live Sessions' }]}
    adminActions={[
      { text: "Edit", icon: Pencil, path: `/admin/live_sessions/${live_session.id}/edit` },
      { text: "Sync with Arlo", icon: RefreshCw, onSelect: syncSession, disabled: $sync.processing },
      { text: "Manage in admin", icon: Settings, path: `/admin/live_sessions/${live_session.id}` },
    ]}
  />

  <Page.Content>
    <Hero session={live_session} />
  </Page.Content>

  <Page.Content>
    
    {#snippet main()}

      <!-- Description -->
      {#if live_session.description?.html}
        <div class="max-w-[80ch] xl:px-5">
          <div class={cn('prose', colors[color]?.selection, colors[color]?.marker)}>
            {@html live_session.description.html}
          </div>
        </div>
      {/if}
    
    {/snippet}

    {#snippet aside()}
      
      <div class="pt-6 flex flex-col gap-6">

        <!-- Presenter -->
        {#if live_session.contributor}
          <ContributorBlock contributor={live_session.contributor} heading="Presented by" />
        {/if}

        <!-- Tags -->
        {#if live_session.tags?.length > 0}
          <Block.Items title="Topics">
            <div class="flex flex-wrap gap-1 py-1">
              {#each live_session.tags as tag (tag.id)}
                <Tag {tag} size="sm" variant="faint" onSelect={() => router.visit(`/live-sessions?tag=${tag.id}`)} />
              {/each}
            </div>
          </Block.Items>
        {/if}

        <!-- Session details -->
        <Block.Items title="Session details">
          <Block.Item label="Date" value={format(live_session.start_time, 'EEE, d MMMM yyyy')} />
          <Block.Item label="Time" value={`${formatTime(live_session.start_time, { mode: '12h' })} – ${formatTime(live_session.end_time, { mode: '12h' })}`} />
          <Block.Item label="Duration" value={duration} />
          {#if live_session.arlo_code}
            <Block.Item label="Session code" value={live_session.arlo_code} />
          {/if}
        </Block.Items>

        {#if canDownloadCalendar}
          <AddToCalendar
            label="Download calendar event (.ics)"
            title={live_session.event_name}
            startTime={live_session.start_time}
            endTime={live_session.end_time}
            description={live_session.description?.text || ''}
            class="text-gray-500/90 underline hover:text-gray-600"
          />
        {/if}

      </div>

    {/snippet}
    
  </Page.Content>

  <!-- Upcoming sessions -->
  {#if upcoming_sessions.length > 0}
    <Page.Content>
      <div class="flex justify-between items-center mb-2">
        <Text variant="title" as="h4" text="Upcoming sessions" />
        <Button variant="link" text="View all" Icon={ChevronRight} path="/live-sessions" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {#each upcoming_sessions as session (session.id)}
          <Card {session} label="Upcoming" />
        {/each}
      </div>
    </Page.Content>
  {/if}
  
</Page.Layout>
<FeedbackFab
<FeedbackFab {feedback} record={{ id: live_session.id, type: "LiveSession", name: live_session.name }} />
